/**
 * SimplifiedV2Mapper - Bidirectional transformation between Simplified V2 and full V2 StepNode
 *
 * Simplified V2: AI-generated schema with plain strings (no LocalizedLabel/LocalizedImage)
 * Full V2 StepNode: Complete schema with translations, appearance, layout
 *
 * Direction 1: SimplifiedV2StepNode → StepNode (for merge with template / rendering)
 * Direction 2: StepNode → SimplifiedV2StepNode (for extracting AI version on content edit)
 *
 * Trigger/Route/RouteGuard pass through unchanged — they have no localized content.
 *
 * @module screen-utils
 */

import type {
  SimplifiedV2StepNode,
  SimplifiedV2Content,
  SimplifiedCopyBlock,
  SimplifiedTapTarget,
  SimplifiedHeaderBar,
  SimplifiedActionBar,
  SimplifiedOptionsList,
  SimplifiedOptionsItem,
  SimplifiedCarouselConfig,
  SimplifiedSlide,
  SimplifiedStepperConfig,
  SimplifiedStepperSegment,
  SimplifiedTextFieldConfig,
  SimplifiedRollerConfig,
  SimplifiedEmbedConfig,
  SimplifiedPaywallConfig,
  AiAsset,
} from '@segmently/schema-models/simplified';

import type {
  StepNode,
  StepContent,
  CopyBlock,
  LocalizedLabel,
  LocalizedImage,
  TapTarget,
  HeaderBar,
  ActionBar,
  OptionsList,
  OptionsItem,
  CarouselConfig,
  StepperConfig,
  TextFieldConfig,
  RollerConfig,
  EmbedConfig,
  PaywallConfig,
  Trigger,
  FeaturedMedia,
  AssetReference,
  TypographyStyle,
} from '@segmently/schema-models/v2';

/**
 * Compatibility-only projection for imports created before the asset-intent
 * pipeline removed inline prompt/media fields from the prompt-facing schema.
 * Runtime conversion still preserves those historical payloads without
 * advertising the fields to new AI producers.
 */
type LegacyAssetContent = SimplifiedV2Content & {
  heroAsset?: AiAsset;
  featuredMediaAsset?: AiAsset;
};
type LegacyAssetOptionsItem = SimplifiedOptionsItem & { imageAsset?: AiAsset };
type LegacyAssetSlide = SimplifiedSlide & { imageAsset?: AiAsset };
type LegacyAssetStepperSegment = SimplifiedStepperSegment & { imageAsset?: AiAsset };

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

/** Create a minimal LocalizedLabel from a plain string */
function wrapLabel(text: string, lang: string): LocalizedLabel {
  return {
    translations: { [lang]: text },
    kind: 'static' as any,
    appearance: {} as TypographyStyle,
  };
}

/** Extract plain string from LocalizedLabel */
function unwrapLabel(label: LocalizedLabel | undefined | null, lang: string): string | undefined {
  if (!label?.translations) return undefined;
  return label.translations[lang] ?? Object.values(label.translations)[0];
}

/** Create a minimal LocalizedImage from a URL */
function wrapImage(url: string, lang: string): LocalizedImage {
  return {
    translations: { [lang]: { original: url } as AssetReference },
    appearance: {} as any,
  };
}

/** Extract URL from LocalizedImage */
function unwrapImage(image: LocalizedImage | undefined | null, lang: string): string | undefined {
  if (!image?.translations) return undefined;
  const ref = image.translations[lang] ?? Object.values(image.translations)[0];
  return (ref as AssetReference)?.original;
}

/** Create a full TapTarget from SimplifiedTapTarget */
function wrapTapTarget(simplified: SimplifiedTapTarget, lang: string): TapTarget {
  return {
    kind: 'label' as any,
    content: wrapLabel(simplified.content, lang),
    appearance: {} as any,
    action: simplified.action,
  };
}

/** Extract SimplifiedTapTarget from full TapTarget */
function unwrapTapTarget(target: TapTarget | undefined | null, lang: string): SimplifiedTapTarget | undefined {
  if (!target) return undefined;
  const content = target.content;
  // TapTargetContent is LocalizedLabel | LocalizedImage
  // For simplified we only care about label text
  let text = '';
  if (content && 'translations' in content) {
    const val = (content as LocalizedLabel).translations?.[lang]
      ?? Object.values((content as LocalizedLabel).translations ?? {})[0];
    text = typeof val === 'string' ? val : '';
  }
  return {
    content: text,
    action: target.action,
  };
}

// ═══════════════════════════════════════════════════════
// SIMPLIFIED → FULL (toStepNode)
// ═══════════════════════════════════════════════════════

function copyCopyBlock(copy: SimplifiedCopyBlock | undefined, lang: string): CopyBlock | undefined {
  if (!copy) return undefined;
  const result: any = {};
  if (copy.title != null) result.title = wrapLabel(copy.title, lang);
  if (copy.subtitle != null) result.subtitle = wrapLabel(copy.subtitle, lang);
  return Object.keys(result).length > 0 ? result : undefined;
}

function copyHeader(header: SimplifiedHeaderBar | undefined, lang: string): HeaderBar | undefined {
  if (!header) return undefined;
  const result: any = {};
  if (header.skip) result.skip = wrapTapTarget(header.skip, lang);
  if (header.back) result.back = wrapTapTarget(header.back, lang);
  if (header.close) result.close = wrapTapTarget(header.close, lang);
  return Object.keys(result).length > 0 ? result : undefined;
}

function copyActionBar(bar: SimplifiedActionBar | undefined, lang: string): ActionBar | undefined {
  if (!bar) return undefined;
  const result: any = {};
  if (bar.primary) result.primary = wrapTapTarget(bar.primary, lang);
  if (bar.secondary) result.secondary = wrapTapTarget(bar.secondary, lang);
  return Object.keys(result).length > 0 ? result : undefined;
}

function copyOptions(options: SimplifiedOptionsList | undefined, lang: string): OptionsList | undefined {
  if (!options?.items) return undefined;
  return {
    items: options.items.map((item) => ({
      title: wrapLabel(item.title, lang),
      subtitle: item.subtitle ? wrapLabel(item.subtitle, lang) : undefined,
      image: (item as LegacyAssetOptionsItem).imageAsset?.generatedUrl
        ? wrapImage((item as LegacyAssetOptionsItem).imageAsset!.generatedUrl!, lang)
        : undefined,
      action: item.action,
      variableValue: item.variableValue,
      weight: item.weight,
    } as OptionsItem)),
    selectionMode: options.selectionMode,
    isRandomized: options.isRandomized,
    maxSelectedItemCount: options.maxSelectedItemCount,
    // W8 (R-W70-37/38): the "More options" disclosure copy is generation's /
    // the owner's, never the compiler's. Plain strings in the simplified shape
    // become the same LocalizedLabel every other option-list label is.
    moreOptionsLabel: options.moreOptionsLabel ? wrapLabel(options.moreOptionsLabel, lang) : undefined,
    fewerOptionsLabel: options.fewerOptionsLabel ? wrapLabel(options.fewerOptionsLabel, lang) : undefined,
  } as OptionsList;
}

function copyCarousel(carousel: SimplifiedCarouselConfig | undefined, lang: string): CarouselConfig | undefined {
  if (!carousel) return undefined;
  return {
    items: carousel.items.map((slide, index) => ({
      ...progressRange(index, carousel.items.length),
      content: {
        title: slide.title ? wrapLabel(slide.title, lang) : undefined,
        subtitle: slide.subtitle ? wrapLabel(slide.subtitle, lang) : undefined,
        detail: slide.subtitle2 ? wrapLabel(slide.subtitle2, lang) : undefined,
        image: (slide as LegacyAssetSlide).imageAsset?.generatedUrl
          ? wrapImage((slide as LegacyAssetSlide).imageAsset!.generatedUrl!, lang)
          : undefined,
      },
    })),
    timer: carousel.timer ? {
      duration: carousel.timer.duration,
      action: carousel.timer.action,
    } : undefined,
  } as CarouselConfig;
}

function copyStepper(stepper: SimplifiedStepperConfig | undefined, lang: string): StepperConfig | undefined {
  if (!stepper) return undefined;
  return {
    items: stepper.items.map((seg, index) => ({
      ...progressRange(index, stepper.items.length),
      content: {
        title: seg.title ? wrapLabel(seg.title, lang) : undefined,
        subtitle: seg.subtitle ? wrapLabel(seg.subtitle, lang) : undefined,
        description: seg.description ? wrapLabel(seg.description, lang) : undefined,
        image: (seg as LegacyAssetStepperSegment).imageAsset?.generatedUrl
          ? wrapImage((seg as LegacyAssetStepperSegment).imageAsset!.generatedUrl!, lang)
          : undefined,
      },
    })),
    timer: stepper.timer ? {
      duration: stepper.timer.duration,
      action: stepper.timer.action,
    } : undefined,
  } as unknown as StepperConfig;
}

function copyTextField(field: SimplifiedTextFieldConfig | undefined, lang: string): TextFieldConfig | undefined {
  if (!field) return undefined;
  return {
    placeholder: wrapLabel(field.placeholder, lang),
    errorMessage: field.errorMessage ? wrapLabel(field.errorMessage, lang) : undefined,
  } as TextFieldConfig;
}

function copyRoller(roller: SimplifiedRollerConfig | undefined, lang: string): RollerConfig | undefined {
  if (!roller) return undefined;
  return {
    wheels: roller.wheels.map((wheel) => ({
      options: wheel.options.map((opt) => ({
        label: wrapLabel(opt.label, lang),
      })),
    })),
  } as unknown as RollerConfig;
}

function copyEmbed(embed: SimplifiedEmbedConfig | undefined): EmbedConfig | undefined {
  if (!embed) return undefined;
  return {
    html: embed.html,
    callback: embed.callback ? { action: embed.callback.action } : undefined,
  } as EmbedConfig;
}

function copyPaywall(paywall: SimplifiedPaywallConfig | undefined, lang: string): PaywallConfig | undefined {
  if (!paywall) return undefined;
  return {
    products: {
      items: paywall.products.map((p) => ({
        productId: p.productId,
        topStartLabel: p.title ? wrapLabel(p.title, lang) : undefined,
        bottomStartLabel: p.subtitle ? wrapLabel(p.subtitle, lang) : undefined,
        badge: p.badge ? { label: wrapLabel(p.badge, lang) } : undefined,
        isSelected: p.isDefault ?? false,
        weight: 0,
      })),
    },
    purchaseAction: paywall.purchaseAction ? wrapTapTarget(paywall.purchaseAction, lang) : undefined,
    closeButton: paywall.closeButton ? wrapTapTarget(paywall.closeButton, lang) : undefined,
    features: paywall.features?.map((f) => ({
      title: f.title ? wrapLabel(f.title, lang) : undefined,
      image: (f as { imageAsset?: AiAsset }).imageAsset?.generatedUrl
        ? wrapImage((f as { imageAsset?: AiAsset }).imageAsset!.generatedUrl!, lang)
        : undefined,
    })),
    restoreLabel: paywall.restoreLabel ? wrapLabel(paywall.restoreLabel, lang) : undefined,
  } as unknown as PaywallConfig;
}

function copyFlexibleLayout(flexibleLayout: any, lang: string): any {
  if (!flexibleLayout) return undefined;
  const sections = Array.isArray(flexibleLayout.sections)
    ? flexibleLayout.sections.map((section: any, index: number) => copyFlexibleSection(section, index, lang))
    : [];
  return {
    scrollable: typeof flexibleLayout.scrollable === 'boolean' ? flexibleLayout.scrollable : true,
    ...(flexibleLayout.insets ? { insets: flexibleLayout.insets } : {}),
    sections,
    ...(flexibleLayout.stickyContainer ? { stickyContainer: flexibleLayout.stickyContainer } : {}),
  };
}

function copyFlexibleSection(section: any, index: number, lang: string): any {
  const kind = flexibleSectionKindToFull(section.kind);
  const result: any = {
    id: typeof section.id === 'string' && section.id ? section.id : `section_${index + 1}`,
    order: typeof section.order === 'number' ? section.order : index,
    kind,
    label: typeof section.label === 'string' ? section.label : undefined,
    layout: section.layout ?? {},
    parentSectionId: typeof section.parentSectionId === 'string' ? section.parentSectionId : undefined,
  };

  if (section.copyContent) {
    result.textContent = copyCopyBlock(section.copyContent, lang);
  }
  if (section.optionsContent) {
    result.optionsListContent = copyOptions({
      ...section.optionsContent,
      selectionMode: section.optionsContent.selectionMode ?? defaultSelectionModeForSectionKind(kind),
    }, lang);
  }
  if (section.buttonContent) {
    result.buttonContent = wrapTapTarget(section.buttonContent, lang);
  }
  if (section.imageContent?.generatedUrl) {
    result.mediaContent = featuredMediaAssetToMedia(section.imageContent, lang);
  }
  if (section.embedContent) {
    result.embedContent = copyEmbed(section.embedContent);
  }
  if (section.paywallHeaderContent) {
    result.paywallHeaderContent = copyPaywallHeader(section.paywallHeaderContent, lang);
  }

  Object.keys(result).forEach((key) => {
    if (result[key] === undefined) {
      delete result[key];
    }
  });
  return result;
}

function copyPaywallHeader(paywallHeader: any, lang: string): any {
  return {
    closeButton: paywallHeader.closeButton ? wrapTapTarget(paywallHeader.closeButton, lang) : undefined,
    restoreLabel: paywallHeader.restoreLabel ? wrapLabel(paywallHeader.restoreLabel, lang) : undefined,
  };
}

function flexibleSectionKindToFull(kind: string | undefined): string {
  const mapping: Record<string, string> = {
    copyBlock: 'Text',
    optionsList: 'OptionsList',
    singleSelectionList: 'SingleSelectionList',
    multipleSelectionList: 'MultipleSelectionList',
    bulletList: 'BulletList',
    button: 'Button',
    image: 'Media',
    customEmbed: 'CustomEmbed',
    emptySpace: 'EmptySpace',
    paywallHeader: 'PaywallHeader',
  };
  return mapping[kind ?? ''] ?? kind ?? 'Text';
}

function flexibleSectionKindToSimplified(kind: string | undefined): string {
  const mapping: Record<string, string> = {
    Text: 'copyBlock',
    OptionsList: 'optionsList',
    SingleSelectionList: 'singleSelectionList',
    MultipleSelectionList: 'multipleSelectionList',
    BulletList: 'bulletList',
    Button: 'button',
    Media: 'image',
    CustomEmbed: 'customEmbed',
    EmptySpace: 'emptySpace',
    PaywallHeader: 'paywallHeader',
  };
  return mapping[kind ?? ''] ?? kind ?? 'copyBlock';
}

function defaultSelectionModeForSectionKind(kind: string): 'one' | 'many' | 'browse' | undefined {
  if (kind === 'SingleSelectionList') return 'one';
  if (kind === 'MultipleSelectionList') return 'many';
  if (kind === 'BulletList') return 'browse';
  return undefined;
}

function progressRange(index: number, total: number): { progressStart: number; progressEnd: number } {
  const count = Math.max(total, 1);
  return {
    progressStart: Math.round((index / count) * 100),
    progressEnd: Math.round(((index + 1) / count) * 100),
  };
}

function heroAssetToImage(asset: AiAsset | undefined, lang: string): LocalizedImage | undefined {
  if (!asset?.generatedUrl) return undefined;
  return wrapImage(asset.generatedUrl, lang);
}

function featuredMediaAssetToMedia(asset: AiAsset | undefined, lang: string): FeaturedMedia | undefined {
  if (!asset?.generatedUrl) return undefined;
  return {
    image: wrapImage(asset.generatedUrl, lang),
  } as unknown as FeaturedMedia;
}

/**
 * Convert SimplifiedV2StepNode to full V2 StepNode.
 * Creates minimal StepNode with content wrapped in LocalizedLabel/LocalizedImage.
 * Styling fields are empty — they come from template merge.
 */
function simplifiedToStepNode(simplified: SimplifiedV2StepNode, lang: string): StepNode {
  const c = simplified.content as LegacyAssetContent;

  const content: StepContent = {
    canvas: { background: {} } as any,
    animated: false,
    offlineFirst: false,
    // Shared
    copy: copyCopyBlock(c.copy, lang),
    hero: heroAssetToImage(c.heroAsset, lang),
    featuredMedia: featuredMediaAssetToMedia(c.featuredMediaAsset, lang),
    header: copyHeader(c.header, lang),
    actionBar: copyActionBar(c.actionBar, lang),
    // Kind-specific
    options: copyOptions(c.options, lang),
    carousel: copyCarousel(c.carousel, lang),
    stepper: copyStepper(c.stepper, lang),
    textField: copyTextField(c.textField, lang),
    roller: copyRoller(c.roller, lang),
    embed: copyEmbed(c.embed),
    paywall: copyPaywall(c.paywall, lang),
    flexibleLayout: copyFlexibleLayout(c.flexibleLayout, lang),
  };

  // Clean undefined fields
  Object.keys(content).forEach((key) => {
    if ((content as any)[key] === undefined) {
      delete (content as any)[key];
    }
  });

  return {
    id: simplified.id,
    name: simplified.name ?? '',
    tags: [],
    screenType: simplified.screenType as any,
    content,
    position: simplified.position ?? { x: 0, y: 0 },
  };
}

// ═══════════════════════════════════════════════════════
// FULL → SIMPLIFIED (toSimplified)
// ═══════════════════════════════════════════════════════

function extractCopy(copy: CopyBlock | undefined, lang: string): SimplifiedCopyBlock | undefined {
  if (!copy) return undefined;
  const result: SimplifiedCopyBlock = {};
  const title = unwrapLabel(copy.title as any, lang);
  const subtitle = unwrapLabel(copy.subtitle as any, lang);
  if (title != null) result.title = title;
  if (subtitle != null) result.subtitle = subtitle;
  return Object.keys(result).length > 0 ? result : undefined;
}

function extractHeader(header: HeaderBar | undefined, lang: string): SimplifiedHeaderBar | undefined {
  if (!header) return undefined;
  const result: SimplifiedHeaderBar = {};
  if (header.skip) result.skip = unwrapTapTarget(header.skip, lang);
  if (header.back) result.back = unwrapTapTarget(header.back, lang);
  if (header.close) result.close = unwrapTapTarget(header.close, lang);
  return Object.keys(result).length > 0 ? result : undefined;
}

function extractActionBar(bar: ActionBar | undefined, lang: string): SimplifiedActionBar | undefined {
  if (!bar) return undefined;
  const result: SimplifiedActionBar = {};
  if (bar.primary) result.primary = unwrapTapTarget(bar.primary, lang);
  if (bar.secondary) result.secondary = unwrapTapTarget(bar.secondary, lang);
  return Object.keys(result).length > 0 ? result : undefined;
}

function extractOptions(options: OptionsList | undefined, lang: string): SimplifiedOptionsList | undefined {
  if (!options?.items?.length) return undefined;
  return {
    selectionMode: options.selectionMode as any,
    isRandomized: options.isRandomized,
    maxSelectedItemCount: options.maxSelectedItemCount,
    moreOptionsLabel: unwrapLabel(options.moreOptionsLabel as any, lang),
    fewerOptionsLabel: unwrapLabel(options.fewerOptionsLabel as any, lang),
    items: options.items.map((item): SimplifiedOptionsItem => ({
      title: unwrapLabel(item.title as any, lang) ?? '',
      subtitle: unwrapLabel(item.subtitle as any, lang),
      imageAsset: item.image ? { generatedUrl: unwrapImage(item.image as any, lang) } : undefined,
      action: item.action as Trigger | undefined,
      variableValue: item.variableValue ?? '',
      weight: item.weight,
    } as LegacyAssetOptionsItem)),
  };
}

function extractCarousel(carousel: CarouselConfig | undefined, lang: string): SimplifiedCarouselConfig | undefined {
  if (!carousel?.items) return undefined;
  return {
    items: carousel.items.map((slide: any): SimplifiedSlide => ({
      title: unwrapLabel(slide.content?.title, lang),
      subtitle: unwrapLabel(slide.content?.subtitle, lang),
      subtitle2: unwrapLabel((slide.content as any)?.detail, lang),
      imageAsset: slide.content?.image ? { generatedUrl: unwrapImage(slide.content.image, lang) } : undefined,
    } as LegacyAssetSlide)),
    timer: carousel.timer ? {
      duration: (carousel.timer as any).duration ?? 0,
      action: (carousel.timer as any).action,
    } : { duration: 0, action: { kind: 'advance', edges: [] } as any },
  };
}

function extractStepper(stepper: StepperConfig | undefined, lang: string): SimplifiedStepperConfig | undefined {
  if (!stepper?.items) return undefined;
  return {
    items: stepper.items.map((seg: any): SimplifiedStepperSegment => ({
      title: unwrapLabel(seg.content?.title ?? seg.title, lang),
      subtitle: unwrapLabel(seg.content?.subtitle ?? seg.subtitle, lang),
      description: unwrapLabel(seg.content?.description ?? seg.description, lang),
      imageAsset: seg.content?.image || seg.image
        ? { generatedUrl: unwrapImage(seg.content?.image ?? seg.image, lang) }
        : undefined,
    } as LegacyAssetStepperSegment)),
    timer: stepper.timer ? {
      duration: (stepper.timer as any).duration ?? 0,
      action: (stepper.timer as any).action,
    } : { duration: 0, action: { kind: 'advance', edges: [] } as any },
  };
}

function extractTextField(field: TextFieldConfig | undefined, lang: string): SimplifiedTextFieldConfig | undefined {
  if (!field) return undefined;
  return {
    placeholder: unwrapLabel(field.placeholder as any, lang) ?? '',
    errorMessage: unwrapLabel(field.errorMessage as any, lang),
  };
}

function extractRoller(roller: RollerConfig | undefined, lang: string): SimplifiedRollerConfig | undefined {
  if (!roller?.wheels) return undefined;
  return {
    wheels: roller.wheels.map((wheel: any) => ({
      options: (wheel.options ?? []).map((opt: any) => ({
        label: unwrapLabel(opt.label, lang) ?? '',
      })),
    })),
  };
}

function extractEmbed(embed: EmbedConfig | undefined): SimplifiedEmbedConfig | undefined {
  if (!embed) return undefined;
  return {
    html: (embed as any).html,
    callback: (embed as any).callback?.action ? { action: (embed as any).callback.action } : undefined,
  };
}

function extractPaywall(paywall: PaywallConfig | undefined, lang: string): SimplifiedPaywallConfig | undefined {
  if (!paywall) return undefined;
  const products = (paywall as any).products?.items ?? [];
  return {
    products: products.map((p: any) => ({
      productId: p.productId,
      title: unwrapLabel(p.topStartLabel ?? p.title, lang),
      subtitle: unwrapLabel(p.bottomStartLabel ?? p.subtitle, lang),
      badge: unwrapLabel(p.badge?.label, lang),
      isDefault: p.isSelected ?? p.isDefault,
    })),
    purchaseAction: unwrapTapTarget((paywall as any).purchaseAction, lang)
      ?? { content: 'Purchase', action: { kind: 'purchase', edges: [] } as any },
    closeButton: unwrapTapTarget((paywall as any).closeButton, lang),
    features: (paywall as any).features?.map((f: any) => ({
      title: unwrapLabel(f.title, lang),
      imageAsset: f.image ? { generatedUrl: unwrapImage(f.image, lang) } : undefined,
    })),
    restoreLabel: unwrapLabel((paywall as any).restoreLabel, lang),
  };
}

function extractFlexibleLayout(flexibleLayout: any, lang: string): any {
  if (!flexibleLayout) return undefined;
  return {
    scrollable: flexibleLayout.scrollable,
    insets: flexibleLayout.insets,
    sections: Array.isArray(flexibleLayout.sections)
      ? flexibleLayout.sections.map((section: any) => extractFlexibleSection(section, lang))
      : [],
    stickyContainer: flexibleLayout.stickyContainer,
  };
}

function extractFlexibleSection(section: any, lang: string): any {
  const result: any = {
    id: section.id,
    order: section.order,
    kind: flexibleSectionKindToSimplified(section.kind),
    label: section.label,
    layout: section.layout,
    parentSectionId: section.parentSectionId,
  };

  if (section.textContent) {
    result.copyContent = extractCopy(section.textContent, lang);
  }
  if (section.optionsListContent) {
    result.optionsContent = extractOptions(section.optionsListContent, lang);
  }
  if (section.buttonContent) {
    result.buttonContent = unwrapTapTarget(section.buttonContent, lang);
  }
  if (section.mediaContent) {
    result.imageContent = mediaToAsset(section.mediaContent, lang);
  }
  if (section.embedContent) {
    result.embedContent = extractEmbed(section.embedContent);
  }
  if (section.paywallHeaderContent) {
    result.paywallHeaderContent = {
      closeButton: unwrapTapTarget(section.paywallHeaderContent.closeButton, lang),
      restoreLabel: unwrapLabel(section.paywallHeaderContent.restoreLabel, lang),
    };
  }

  Object.keys(result).forEach((key) => {
    if (result[key] === undefined) {
      delete result[key];
    }
  });
  return result;
}

function imageToHeroAsset(hero: LocalizedImage | undefined, lang: string): AiAsset | undefined {
  const url = unwrapImage(hero, lang);
  if (!url) return undefined;
  return { generatedUrl: url };
}

function mediaToAsset(media: FeaturedMedia | undefined, lang: string): AiAsset | undefined {
  if (!media) return undefined;
  const url = unwrapImage((media as any).image, lang);
  if (!url) return undefined;
  return { generatedUrl: url };
}

/**
 * Convert full V2 StepNode to SimplifiedV2StepNode.
 * Strips all styling/appearance, extracts plain strings from LocalizedLabel.
 */
function stepNodeToSimplified(stepNode: StepNode, lang: string): SimplifiedV2StepNode {
  const c = stepNode.content;

  const content: LegacyAssetContent = {};

  const copy = extractCopy(c.copy, lang);
  if (copy) content.copy = copy;

  const heroAsset = imageToHeroAsset(c.hero, lang);
  if (heroAsset) content.heroAsset = heroAsset;

  const featuredMediaAsset = mediaToAsset(c.featuredMedia, lang);
  if (featuredMediaAsset) content.featuredMediaAsset = featuredMediaAsset;

  const header = extractHeader(c.header, lang);
  if (header) content.header = header;

  const actionBar = extractActionBar(c.actionBar, lang);
  if (actionBar) content.actionBar = actionBar;

  const options = extractOptions(c.options, lang);
  if (options) content.options = options;

  const carousel = extractCarousel(c.carousel, lang);
  if (carousel) content.carousel = carousel;

  const stepper = extractStepper(c.stepper, lang);
  if (stepper) content.stepper = stepper;

  const textField = extractTextField(c.textField, lang);
  if (textField) content.textField = textField;

  const roller = extractRoller(c.roller, lang);
  if (roller) content.roller = roller;

  const embed = extractEmbed(c.embed);
  if (embed) content.embed = embed;

  const paywall = extractPaywall(c.paywall, lang);
  if (paywall) content.paywall = paywall;

  const flexibleLayout = extractFlexibleLayout(c.flexibleLayout, lang);
  if (flexibleLayout) content.flexibleLayout = flexibleLayout;

  return {
    id: stepNode.id,
    name: stepNode.name,
    screenType: stepNode.screenType as any,
    content,
    position: stepNode.position,
  };
}

// ═══════════════════════════════════════════════════════
// DETECTION
// ═══════════════════════════════════════════════════════

/**
 * Detect if a node is in SimplifiedV2 format (plain strings) vs full V2 (LocalizedLabel).
 * Checks the first non-null text field found.
 */
function isSimplifiedV2StepNode(node: any): boolean {
  // Check copy.title
  const copy = node?.content?.copy;
  if (copy) {
    if (copy.title !== undefined) return typeof copy.title === 'string';
    if (copy.subtitle !== undefined) return typeof copy.subtitle === 'string';
  }

  // Check options items
  const items = node?.content?.options?.items;
  if (items?.length > 0) {
    return typeof items[0].title === 'string';
  }

  // Check actionBar primary button content
  const primary = node?.content?.actionBar?.primary;
  if (primary) {
    return typeof primary.content === 'string';
  }

  // Check heroAsset (simplified) vs hero (full)
  if (node?.content?.heroAsset !== undefined) return true;
  if (node?.content?.hero !== undefined) return false;

  return false;
}

// ═══════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════

export class SimplifiedV2Mapper {
  /**
   * Convert SimplifiedV2StepNode → full V2 StepNode.
   * Creates minimal StepNode with content wrapped in LocalizedLabel/LocalizedImage.
   * Styling fields are empty — they come from template merge.
   */
  toStepNode(simplified: SimplifiedV2StepNode, lang: string): StepNode {
    return simplifiedToStepNode(simplified, lang);
  }

  /**
   * Convert full V2 StepNode → SimplifiedV2StepNode.
   * Strips all styling/appearance, extracts plain strings from LocalizedLabel.
   */
  toSimplified(stepNode: StepNode, lang: string): SimplifiedV2StepNode {
    return stepNodeToSimplified(stepNode, lang);
  }

  /**
   * Detect if a node is in SimplifiedV2 format (plain strings) vs full V2 (LocalizedLabel objects).
   */
  isSimplified(node: any): boolean {
    return isSimplifiedV2StepNode(node);
  }
}

/** Singleton instance */
export const simplifiedV2Mapper = new SimplifiedV2Mapper();
